import { Button } from '@mui/material';
import { calculateLocatinVars } from '../../../libs/browser';
import { getGrecaptchaToken, reloadAccountProps } from '../../../libs/state';
import { parseError } from '../../../libs/parser';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Context from '../../../provider';
import Countdown from '../../../components/Countdown';
import IconSettingTag from '../../../assets/hkdc/icons/SettingTagIcon';
import LogoFooterComponent from '../../../components/LogoFooterComponent';
import OtpInput from 'react-otp-input';
import PageTitleContComponent from '../../../components/PageTitleContComponent';
import React, { useContext, useEffect, useState } from 'react';
import config from '../../../config';
import memberApi from '../../../libs/api/memberApi';
import useNotificationsCount from '../../../hooks/useNotificationsCount';
import './index.scss';

export default function WebEmailOtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationVars = calculateLocatinVars(location.pathname);

  const notificationNotReadCount = useNotificationsCount();

  const { init, dispatch, me, appConfig } = useContext(Context);

  const [otpInputElements, setOtpInputElements] = useState([]);

  const [otp, setOtp] = useState('');
  const [otpErr, setOtpErr] = useState(false);
  const [nextOtpInterval, setNextOtpInterval] = useState(0);
  const [otpSeconds, setOtpSeconds] = useState(0);

  useEffect(() => {
    setNextOtpInterval(appConfig?.['otp_send_interval'] || 120);
    setOtpSeconds(appConfig?.['otp_send_interval'] || 120);
  }, []);

  // This page only allow logged in user
  useEffect(() => {
    if (init && me) {
      if (location.state?.type === 'login' || location.state?.type === 'forgot-email') {
        navigate(`${config.basename}registration`, { replace: true });
      } else if (me.status === 'activated') {
        if (location.state?.type === 'login' || location.state?.type === 'forgot-email') {
          navigate(`${config.basename}`, { replace: true });
        }
      }
    }

    // 获取所有 class 为 'otp-input' 的元素
    const inputElements = document.querySelectorAll('.otp-input');
    setOtpInputElements(inputElements);

  }, [init, me, navigate]);

  useEffect(() => {
    // 为每个元素添加 onFocus 事件监听器
    otpInputElements.forEach((element) => {
      element.addEventListener('focus', (event) => {
        // 这里可以添加其他需要在获得焦点时执行的代码
        setOtpErr(false);
      });
    });
  }, [otpInputElements]);

  const resendOtp = async (e) => {
    e.preventDefault();
    await sendOtp({ email: location.state?.username, username: location.state?.username });
  };

  const sendOtp = async (loginData) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      await memberApi.requestOtp({
        ...loginData,
        ...(appConfig?.recaptcha ? { gRecaptchaResponse: await getGrecaptchaToken(appConfig?.recaptcha) } : {})
      });
      setNextOtpInterval(0);
      setNextOtpInterval(appConfig?.['otp_send_interval'] || 120);
      setOtpSeconds(appConfig?.['otp_send_interval'] || 120);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const onOtpChange = async (otp) => {
    setOtp(otp);
    // Submit to login if otp is 6 digits
    if (String(otp).length === 6) {
      if (location.state?.type === 'email-edit') {
        submitTokenOtp(otp);
      } else {
        ConfirmEmailByOtp(otp);
      }
    }
  };

  const submitTokenOtp = async (otp) => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const response = await memberApi.updateRequestIdentity({
        token: location.state?.token,
        otp
      });

      if (response) {
        dispatch({ type: 'SET_ME', payload: {
          ...me,
          email: location.state?.username
        } });

        navigate(`${config.basename}success`, {
          replace: true,
          state: {
            title: 'success.change-email-tile',
            desc: 'success.change-email-desc'
          }
        });
      }
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  /**
   * 验证邮箱验证码
   */
  const ConfirmEmailByOtp = async (otp) => {

    try {
      const username = location.state?.username;
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      await memberApi.login({
        username,
        otp,
        grantType: 'otp',
        clientId: appConfig.auth_client_id,
        ...(appConfig?.recaptcha ? { gRecaptchaResponse: await getGrecaptchaToken(appConfig?.recaptcha) } : {})
      });
      await checkLoginStatus();
    } catch (error) {
      // 在这时，要把opt的输入框置红色
      if (error.response.status === 400) {
        setOtp('');
        otpInputElements.forEach((item) => item.blur());
        setOtpErr(true);
        return;
      }
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  const checkLoginStatus = async () => {
    try {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: true });
      const me = await memberApi.getMe();
      switch (me?.status) {
        case 'new':
        case 'activated':
          await reloadAccountProps({ dispatch, me });
          break;
        case 'disabled':
          dispatch({
            type: 'SET_ERROR_DIALOG',
            payload: { open: true, title: t('login.loginFailed'), message: t('login.loginFailedDisabled') }
          });
          break;
        default:
          dispatch({
            type: 'SET_ERROR_DIALOG',
            payload: { open: true, title: t('login.loginFailed'), message: t('login.loginFailedAbnormal') }
          });
      }
    } catch (error) {
      dispatch({
        type: 'SET_ERROR_DIALOG',
        payload: { open: true, ...parseError(error) }
      });
    } finally {
      dispatch({ type: 'SET_BACKDROP_LOADING', payload: false });
    }
  };

  return (
    (
      <div className='web-email-otp-page'>
        <div className="white-block content-container">
          <div className="black-block full-width">
            <PageTitleContComponent
              pageTitleIcon={
                <IconSettingTag
                  className="setting-tag-icon"
                  size="large" />
              }
            />
          </div>

          <div className="section-max-container setting-content-container">
            <div className="otp-cont">
              <div className="title-content-cont">
                  <div className="title-text text-font-family">
                    {t('otp.desc')}
                  </div>
                  <div className="content-text text-font-family">
                    {
                      location.state.type === 'forgot-email' ?
                      location.state.username.replace(/(.{3})(?=@)/, '******') :
                      location.state.username
                    }
                  </div>
              </div>
              <div className="opt-input-cont">
                <OtpInput
                  containerStyle="otp-control-input"
                  value={otp}
                  onChange={onOtpChange}
                  numInputs={6}
                  inputType="number"
                  shouldAutoFocus="true"
                  inputStyle={{
                    border: otpErr ? '1px solid #E53323' : undefined,
                    outline: 'none'
                  }}
                  renderInput={(props, index) =>
                    {
                      props.className = 'otp-input';

                      return (
                        <input key={index} {...props} />
                      );
                    }
                  }
                />
                {
                  otpErr &&
                  <div className="error-desc">
                    {t('otp.error')}
                  </div>
                }
              </div>
              <div className={`resend-top-container${otpSeconds === 0 ? ' enable-resend' : ' disable-resend'}`}>
                  <p>{`${t('otp.otpEmailNotReceived')}`}&nbsp;</p>
                  {nextOtpInterval > 0 && <Countdown
                    className="otp-resend-countdown"
                    initialSeconds={nextOtpInterval}
                    onSecondsChange = {(seconds) => {
                      setOtpSeconds(seconds);
                    }}
                    countdownString={t('otp.otpResendCountDown')}
                    domAfterZero={
                      <form onSubmit={resendOtp}>
                        <Button type="submit" variant="text">
                          {t('otp.otpResend')}
                        </Button>
                      </form>
                    } />
                  }
                </div>
            </div>
          </div>

          <div className="black-block full-width">
            <LogoFooterComponent />
          </div>
        </div>
      </div>
    )
  );
}